import { useState } from 'react'
import { useParams } from 'react-router'
import {
  Col,
  Form,
  Modal as ModalAntd,
  notification,
  Radio,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons'
import _ from 'lodash'
import classNames from 'classnames'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Modal from 'components/Modal'
import Input from 'components/Input'
import Select from 'components/Select'
import Table from 'components/Table'
import TextArea from 'components/TextArea'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import {
  getReportHeaderValueApi,
  createReportHeaderValueApi,
  updateReportHeaderValueByIdApi,
  deleteReportHeaderValueByIdApi,
} from 'services/report_header_attr'
import { getValueListApi, getValueListTypeApi } from 'services/value_list'
import { getBuApi } from 'services/filter/bu'

import Styled from './styled'
import { useAsync } from 'react-use'

export default function PageReportHeaderAttributeSetup () {
  const { t } = useTranslate()
  const { id } = useParams()

  const [form] = Form.useForm()

  const [content, setContent] = useState({
    pageTitle: '',
    headerTitle: '',
    searchText: '',
    buttonText: '',
  })

  const [dataSource, setDataSource] = useState([])
  const [buId, setBuId] = useState(0)
  const [impactId, setImpactId] = useState(0)
  const [likelihood, setLikelihood] = useState(0)
  const [editingId, setEditingId] = useState(null)
  const [filters, setFilters] = useState({
    param: null,
    is_active: null,
  })
  const [status, setStatus] = useState('all') // all, active, inactive
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [allBu, setAllBu] = useState([])
  const [allImpactLevel, setAllImpactLevel] = useState([])

  const getContent = () => {
    switch (id) {
      case '1':
        setContent({
          pageTitle: t('Business Process'),
          headerTitle: t('Business Process'),
          searchText: t('Search Business Process Name or Description'),
          buttonText: t('Create New Business Process'),
        })
        break
      case '2':
        setContent({
          pageTitle: t('Impact Level'),
          headerTitle: t('Impact Level'),
          searchText: t('Search Impact Level Name or Description'),
          buttonText: t('Create New Impact Level'),
        })
        break
      case '3':
        setContent({
          pageTitle: t('Likelihood Level'),
          headerTitle: t('Likelihood Level'),
          searchText: t('Search Likelihood Level Name or Description'),
          buttonText: t('Create New Likelihood Level'),
        })
        setLikelihood(1)
        break
      default:
        break
    }
  }

  const handleChange = (param) => (value) => {
    if (param === 'BU') setBuId(value)
    else if (param === 'ImpactLevel') setImpactId(value)
  }

  const getImpactLevelList = async () => {
    const valueListType = await getValueListTypeApi()
    const targetType = _.find(valueListType.data, (item) => item.value_list_type_code === 'IPT')
    const impactLevelList = await getValueListApi(targetType.value_list_type_id)
    const formatImpactLevel = _.map(impactLevelList.data, (item) => {
      return { label: item.name, value: item.id }
    })
    setAllImpactLevel(formatImpactLevel)
  }

  const getBuList = async () => {
    const buList = await getBuApi()
    const formatBu = _.map(buList.data, (item) => {
      return { label: item.name, value: item.id }
    })
    setAllBu(formatBu)
  }

  const getReportHeaderValue = async () => {
    if (isLoading) return
    else if (!buId && !impactId && !likelihood) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setIsLoading(true)

    const res = await getReportHeaderValueApi(id, buId, impactId, params)

    setIsLoading(false)

    setDataSource(res.data?.items)
  }

  const createReportHeaderValue = async (values) => {
    const res = await createReportHeaderValueApi({
      report_header_type_id: id,
      report_hd_att_val_name: values.name,
      report_hd_att_val_desc: values.description,
      report_hd_att_val_score: values.score,
      is_active: values.is_active,
      is_significant: values.significant,
      bu_id: id === '1' ? buId : null,
      impact_type_id: id === '2' ? impactId : null,
    })

    if (res.status === 'error') {
      notification.error({
        message: t('Failed'),
        description: res.message,
      })
      return
    } else {
      notification.success({
        message: t('Created Success'),
        description: res.message,
      })
      getReportHeaderValue()
    }

    setShowCreateModal(false)

    form.resetFields()
  }

  const updateReportHeaderValue = async (id, values) => {
    const res = await updateReportHeaderValueByIdApi({
      report_hd_att_val_id: id,
      report_hd_att_val_name: values.name,
      report_hd_att_val_desc: values.description,
      report_hd_att_val_score: values.score,
      is_active: values.is_active,
      is_significant: values.significant,
      impact_type_id: impactId,
    })

    if (res.status === 'error') {
      notification.error({
        message: t('Failed'),
        description: res.message,
      })
      return
    } else {
      notification.success({
        message: t('Updated Success'),
        description: res.message,
      })
      getReportHeaderValue()
    }

    setShowCreateModal(false)

    form.resetFields()
  }

  const deleteReportHeaderValue = async (id) => {
    const res = await deleteReportHeaderValueByIdApi(id)

    if (res.status === 'error') {
      notification.error({
        message: t('Failed'),
        description: res.message,
      })
      return
    } else {
      notification.success({
        message: t('Deleted Success'),
        description: res.message,
      })
      getReportHeaderValue()
    }
  }

  useAsync(async () => {
    getContent()
    if (id === '1') getBuList()
    else if (id === '2') getImpactLevelList()
    getReportHeaderValue()
  }, [buId, impactId, likelihood, filters, status])

  const columns = [
    {
      title: t('Title'),
      dataIndex: 'name',
      key: 'name',
      className: 'bold',
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('Score'),
      dataIndex: 'score',
      key: 'score',
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      render: (text, record) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.is_active,
              'is-inactive': !record.is_active,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record.id)

                  const editingDataSource = _.find(dataSource, { id: record.id })
                  form.setFieldsValue({
                    name: editingDataSource.name,
                    description: editingDataSource.description,
                    score: editingDataSource.score,
                    significant: editingDataSource.significant[0],
                    is_active: editingDataSource.is_active,
                  })

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(dataSource, { id: record.id })

                  ModalAntd.confirm({
                    title: t('Confirm deletion: Header Attribute'),
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>{t('Header Name')}</dt>
                          <dd>- {deletingDataSource.name}</dd>
                          <dt>{t('Header Description')}</dt>
                          <dd>- {deletingDataSource.description}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: t('Delete'),
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: t('Cancel'),
                    onOk: () => {
                      deleteReportHeaderValue(deletingDataSource.id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-report-header-attribute'>
      <Page
        title={t(content.pageTitle)}
        searchPlaceholder={t(content.searchText)}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col>
              <Space size='middle'>
                {id === '3' ? (
                  ''
                ) : id === '1' ? (
                  <Select
                    placeholder={t('Select BU/SBU')}
                    onChange={handleChange('BU')}
                    options={allBu}
                  />
                ) : (
                  <Select
                    placeholder={t('Select Impact Level')}
                    onChange={handleChange('ImpactLevel')}
                    options={allImpactLevel}
                  />
                )}
              </Space>
            </Col>
            <Col>
              <Button
                type='primary'
                size='large'
                onClick={() => setShowCreateModal(true)}
                disabled={buId ? false : impactId ? false : likelihood ? false : true}
              >
                {t(content.buttonText)}
              </Button>
            </Col>
          </Row>
          <Card title={t(content.headerTitle)}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={isLoading}
              columns={columns}
              dataSource={dataSource}
              scroll={{ x: 800 }}
              pagination={false}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={editingId ? t('Update Key-Value') : t(content.buttonText)}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {editingId ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              is_active: true,
            }}
            onFinish={async (values) => {
              if (editingId) {
                updateReportHeaderValue(editingId, values)
              } else {
                createReportHeaderValue(values)
              }
            }}
          >
            <Row gutter={8}>
              <Col span={18}>
                <Form.Item
                  label={t('Title')}
                  name='name'
                  rules={[{ required: true, message: t('Please input title') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('Score')}
                  name='score'
                  rules={[{ required: true, message: t('Please input score') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={t('Description')}
              name='description'
              rules={[{ required: true, message: t('Please input description') }]}
            >
              <TextArea />
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Form.Item
                  name='significant'
                  rules={[{ required: true, message: t('Please select significant') }]}
                >
                  <Radio.Group value={form.value}>
                    <Radio value={1}>{t('Significant')}</Radio>
                    <Radio value={0}>{t('Non-significant')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col>
                <Form.Item name='is_active'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
